import { Color2, createCanvas } from 'outpost';
import { renderText } from './text-rendering.ts';
import { AssetLoader } from './asset-loader.ts';
import { isImageUrl, normalizeString } from './utils.ts';
import { PREVIEW_CARD_HEIGHT_PIXELS, PREVIEW_CARD_WIDTH_PIXELS, PDF_CARD_HEIGHT_PIXELS, PDF_CARD_WIDTH_PIXELS } from './constants.ts';
import { FontSize, HorizontalAlign, VerticalAlign } from './types.ts';
import { Project } from './project.ts';
import { CardData } from './project-types.ts';

export type CardImageData = {
    canvas: HTMLCanvasElement;
    quantity: number;
};

export type GenerateCardImageParams = {
    imageLoader: AssetLoader;
    pdfQuality: boolean;
};

const HORIZONTAL_ALIGN_MULTIPLIER: { [Key in HorizontalAlign]: number } = { left: 0, center: 0.5, right: 1 };
const VERTICAL_ALIGN_MULTIPLIER: { [Key in VerticalAlign]: number } = { top: 0, middle: 0.5, bottom: 1 };

export async function generateCardImages(project: Project, params: GenerateCardImageParams): Promise<CardImageData[]> {
    let { imageLoader, pdfQuality } = params;
    let result: Promise<CardImageData>[] = [];
    let preloadPromises: Promise<any>[] = [];

    preloadPromises.push(imageLoader.loadFont('montserrat', 'assets/fonts/montserrat.ttf'));

    for (let content of Object.values(project.replacements)) {
        preloadImage(content, imageLoader, preloadPromises);
    }

    for (let mapping of Object.values(project.mappings)) {
        for (let content of Object.values(mapping)) {
            preloadImage(content, imageLoader, preloadPromises);
        }
    }

    for (let card of Object.values(project.cards)) {
        for (let content of Object.values(card.columns)) {
            preloadImage(content, imageLoader, preloadPromises);
        }
    }

    await Promise.all(preloadPromises);

    for (let card of project.cards) {
        let quantity = card.quantity;

        if (quantity <= 0) {
            continue;
        }

        let promise = drawCard(card, project, imageLoader, pdfQuality).then(canvas => ({ canvas, quantity }));

        result.push(promise);
    }

    return await Promise.all(result);
}

function preloadImage(content: string, imageLoader: AssetLoader, preloadPromises: Promise<any>[]) {
    let match = content.match(/https:.*\.png/);

    if (match) {
        preloadPromises.push(imageLoader.loadImage(match[0]));
    }
}

async function drawCard(card: CardData, project: Project, assetLoader: AssetLoader, pdfQuality: boolean): Promise<HTMLCanvasElement> {
    let canvasWidth = pdfQuality ? PDF_CARD_WIDTH_PIXELS : PREVIEW_CARD_WIDTH_PIXELS;
    let canvasHeight = pdfQuality ? PDF_CARD_HEIGHT_PIXELS : PREVIEW_CARD_HEIGHT_PIXELS;
    let canvas = createCanvas({ width: canvasWidth, height: canvasHeight });
    let ctx = canvas.getContext('2d')!;
    let { width: templateWidth, height: templateHeight } = project.panels[0].rect;
    let scaleX = canvasWidth / templateWidth;
    let scaleY = canvasHeight / templateHeight;

    for (let { rect, content } of project.panels) {
        let x = rect.x * scaleX | 0;
        let y = rect.y * scaleY | 0;
        let width = rect.width * scaleX | 0;
        let height = rect.height * scaleY | 0;

        content = formatContent(content, card, project);

        if (content.startsWith('$COLOR_HASH')) {
            let color: string;

            if (content === '$COLOR_HASH_DARK') {
                color = generateColorFromCard(card, color => color.getLuminance() < 0.7);
            } else if (content === '$COLOR_HASH_LIGHT') {
                color = generateColorFromCard(card, color => color.getLuminance() > 0.3);
            } else {
                color = generateColorFromCard(card);
            }

            drawRectangle(ctx, { x, y, width, height, backgroundColor: color });
        } else if (Color2.isColor(content)) {
            drawRectangle(ctx, { x, y, width, height, backgroundColor: content });
        } else if (isImageUrl(content)) {
            let image = await assetLoader.loadImage(content);

            drawRectangle(ctx, { x, y, width, height, image });
        } else {
            let { align, size, text } = parseContent(content);

            if (text === '-') {
                align = 'center';
            }

            drawText(ctx, {
                imageLoader: assetLoader,
                x,
                y,
                width,
                height,
                text,
                textFont: project.font.name,
                textColor: project.font.color,
                textSize: Math.round(computeTextSize(size, height / scaleY) * scaleY),
                textHorizontalAlign: align,
                textVerticalAlign: 'middle',
                textBold: false,
                textItalic: false,
            });
        }
    }

    if (!pdfQuality) {
        card.image = canvas;
        card.imageId = crypto.randomUUID();
    }

    return canvas;
}

const ALIGN_MAPPING: { [key: string]: HorizontalAlign; } = { '>': 'left', '<': 'right', '|': 'center' };

function parseContent(content: string): { align: HorizontalAlign; size: FontSize; text: string; } {
    let size: FontSize = '80%';
    let align: HorizontalAlign = 'center';
    let text = content;

    let match = content.match(/(\d+(?:px|%))?\s*([<>|])?\s*(.*)/s);

    if (match) {
        size = match[1] as FontSize ?? size;
        align = ALIGN_MAPPING[match[2]] ?? align;
        text = match[3] ?? text;
    }

    return { align, size, text };
}

type DrawTextParams = {
    imageLoader: AssetLoader;
    x: number;
    y: number;
    width: number;
    height: number;
    text: string;
    textFont: string;
    textSize: number;
    textColor: string;
    textHorizontalAlign: HorizontalAlign;
    textVerticalAlign: VerticalAlign;
    textBold: boolean;
    textItalic: boolean;
};

function drawText(ctx: CanvasRenderingContext2D, params: DrawTextParams) {
    if (!params.text) {
        return;
    }

    let textImage = renderText({
        imageLoader: params.imageLoader,
        content: params.text,
        width: params.width,
        height: params.height,
        font: params.textFont,
        size: params.textSize,
        color: params.textColor,
        bold: params.textBold,
        italic: params.textItalic,
        horizontalAlign: params.textHorizontalAlign,
        verticalAlign: params.textVerticalAlign,
        padding: 0,
        allowMultiline: true,
    }).image;

    let mx = HORIZONTAL_ALIGN_MULTIPLIER[params.textHorizontalAlign] ?? 0.5;
    let my = VERTICAL_ALIGN_MULTIPLIER[params.textVerticalAlign] ?? 0.5;
    let offsetX = (params.width - textImage.width) * mx;
    let offsetY = (params.height - textImage.height) * my;

    ctx.drawImage(textImage, params.x + offsetX, params.y + offsetY);
}

function computeTextSize(textSize: FontSize, boxHeight: number): number {
    let value = parseInt(textSize);

    if (textSize.endsWith('%')) {
        return Math.round(value / 100 * boxHeight);
    } else {
        return value;
    }
}

function formatColor(color: string | null): string | null {
    if (color === 'none') {
        return null;
    } else {
        return color;
    }
}

type DrawRectangleParams = {
    x: number;
    y: number;
    width: number;
    height: number;
    backgroundColor?: string | null;
    borderColor?: string | null;
    borderRadius?: number;
    borderWidth?: number;
    image?: HTMLImageElement | null;
    imageScale?: number;
    imageFit?: boolean;
    imageCrop?: boolean;
};

function drawRectangle(ctx: CanvasRenderingContext2D, params: DrawRectangleParams) {
    let {
        x,
        y,
        width,
        height,
        backgroundColor = null,
        borderRadius = 0,
        borderColor = null,
        borderWidth = 0,
        image = null,
        imageScale = 1,
        imageFit = false,
        imageCrop = true,
    } = params;

    backgroundColor = formatColor(backgroundColor);
    borderColor = formatColor(borderColor);

    if (!borderColor && !backgroundColor && !image) {
        return;
    }

    let r = borderRadius;
    let x1 = x;
    let y1 = y;
    let x2 = x + width;
    let y2 = y + height;
    let drawBorder = borderWidth && borderColor;
    let drawImage = image && image.width > 0 && image.height > 0;
    let doClip = drawBorder || drawImage;

    doClip = false;

    if (borderRadius === 0) {
        ctx.beginPath();
        ctx.rect(x, y, width, height);
    } else {
        ctx.beginPath();
        ctx.moveTo(x1 + r, y1);
        ctx.lineTo(x2 - r, y1);
        ctx.quadraticCurveTo(x2, y1, x2, y1 + r);
        ctx.lineTo(x2, y2 - r);
        ctx.quadraticCurveTo(x2, y2, x2 - r, y2);
        ctx.lineTo(x1 + r, y2);
        ctx.quadraticCurveTo(x1, y2, x1, y2 - r);
        ctx.lineTo(x1, y1 + r);
        ctx.quadraticCurveTo(x1, y1, x1 + r, y1);
        ctx.closePath();
    }

    if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fill();
    }

    if (doClip) {
        ctx.save();
        ctx.clip();
    }

    if (image && drawImage) {
        let imageAspectRatio = image.width / image.height;
        let boxAspectRatio = width / height;
        let sWidth = image.width;
        let sHeight = image.height;
        let dWidth = width;
        let dHeight = height;

        if (!imageFit) {
            if (imageCrop) {
                if (imageAspectRatio > boxAspectRatio) {
                    sWidth = image.height * boxAspectRatio;
                } else {
                    sHeight = image.width / boxAspectRatio;
                }
            } else {
                if (imageAspectRatio > boxAspectRatio) {
                    dHeight = width / imageAspectRatio;
                } else {
                    dWidth = height * imageAspectRatio;
                }
            }
        }

        dWidth = Math.round(dWidth * imageScale);
        dHeight = Math.round(dHeight * imageScale);

        let sx = (image.width - sWidth) / 2;
        let sy = (image.height - sHeight) / 2;
        let dx = x + (width - dWidth) / 2;
        let dy = y + (height - dHeight) / 2;

        ctx.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
    }

    if (drawBorder && borderColor) {
        ctx.lineWidth = borderWidth * 2;
        ctx.strokeStyle = borderColor;
        ctx.stroke();
    }

    if (doClip) {
        ctx.restore();
    }
}

function formatContent(content: string, card: CardData, project: Project): string {
    let shouldHide = false;

    content = content.replace(/{[^/}=]+(\/[^}]+)*}/g, str => {
        let items = str.substring(1, str.length - 1).split('/');
        let columnName = normalizeString(items[0]);
        let replacement = card.columns[columnName];
        let options = items.slice(1);

        if (columnName === '#') {
            replacement = card.number.toString();
        }

        if (replacement) {
            for (let option of options) {
                if (option === 'lower') {
                    replacement = replacement.toLowerCase();
                } else if (option === 'upper') {
                    replacement = replacement.toUpperCase();
                }
            }
        } else {
            shouldHide = true;
        }

        return replacement ?? str;
    });

    content = content.replace(/{[^/}=]+\s*=>\s*[^/}=]+}/g, str => {
        let result = str;
        let [columnName, metadata] = str.substring(1, str.length - 1).split('=>').map(str => str.trim());
        let value = card.columns[normalizeString(columnName)];
        let replacement = project.mappings[metadata]?.[value];

        if (replacement) {
            result = replacement;
        }

        return result;
    });

    for (let source in project.replacements) {
        if (!isImageUrl(source)) {
            content = content.replaceAll(source, project.replacements[source]);
        }
    }

    content = content.replaceAll('\\n', '\n');

    content = content.replace(/\n(?!-)/g, '\n\n');

    if (shouldHide) {
        content = '';
    }

    return content;
}

function generateColorFromCard(card: CardData, predicate?: (color: Color2) => boolean): string {
    let content = Object.values(card.columns).join(',');
    let color = Color2.fromStringHash(content);
    let i = 0;

    while (!(predicate?.(color) ?? true) && i < 20) {
        content += 'X';
        color = Color2.fromStringHash(content);
        i += 1;
    }

    return color.toHexString();
}